import moment from 'moment';
import { mergeWith } from 'lodash';
import { useMutation } from '@tanstack/react-query';

import { CustomNotification } from 'common/components';

import { useDispatchReloadPage } from 'hooks/useReloadPage';
import { getManageScheduleDownloadKey } from 'hooks/folders/queries';

import { folderDownload, configScheduleDownload } from 'services/folder';

import * as api from 'config/axios';

import { FOLDER_DOWNLOAD } from 'services/folder/endpoints';
import { EVENT } from 'static/Constants';

export const DOWNLOAD_FOLDER_DEFAULT_SETTING = {
  productCropSize: null,
  productResolution: null,
  productBackground: 'Original',
  digitalAssetImageFormat: 'ORIGINAL',
  digitalAssetResolution: null,
  memberDefinedSheets: 'VAULT Default',
  keepFolderStructure: true,
  includeSubfolders: true,
  runAt: moment('00:00:00', 'HH:mm:ss'),
  runEvery: 0,
  interval: 'Daily',
  timezone: 'America/Detroit',
};

const downloadFolderDirect = (params) => {
  return api.requestToDownload({
    method: 'POST',
    apiEndpoint: FOLDER_DOWNLOAD,
    payload: params,
    options: { timeout: 1800000 },
  });
};

export const useDownloadFolder = (props) => {
  const { form, closeModal } = props;

  const downloadFolderMutation = useMutation({
    mutationKey: ['download-folder'],
    mutationFn: async (params) => {
      try {
        if (params.deliveryMethod === 'direct')
          return downloadFolderDirect(params);

        //* download via email
        return folderDownload(params);
      } catch (error) {
        console.log('error: ', error);
      }
    },
  });

  const initDownloadSetting = (downloadSetting) => {
    const initValues = mergeWith(
      { ...downloadSetting },
      DOWNLOAD_FOLDER_DEFAULT_SETTING,
      (downloadSettingValue, defaultValue) =>
        downloadSettingValue ? downloadSettingValue : defaultValue
    );

    const formattedValues = {
      ...initValues,
      productImageFormat: initValues?.productImageFormat?.toUpperCase(),
      digitalAssetImageFormat:
        initValues?.digitalAssetImageFormat.toUpperCase(),
      queryDataFormat: initValues?.queryDataFormat?.toUpperCase(),
    };

    form.setFieldsValue({
      ...formattedValues,
    });
  };

  const initProductMapping = (mappingData) => {
    form.setFieldsValue({
      selectedMapping: mappingData,
      productDataFormat: mappingData?.formatTypes,
    });
  };

  const downloadFolder = async (params) => {
    const { selectedMapping, ...restParams } = params;

    const downloadFolderResponse = await downloadFolderMutation.mutateAsync({
      ...restParams,
      mappingId: selectedMapping?.id,
    });

    if (downloadFolderResponse?.isSuccess) {
      CustomNotification.success(
        downloadFolderResponse?.message || 'Download Folder Successfully'
      );

      closeModal();

      return;
    }

    CustomNotification.error(
      downloadFolderResponse?.message || 'Download Folder Fail'
    );
  };

  const downloadViaEmails = (emails) => {
    const formValues = form.getFieldsValue();
    downloadFolder({ ...formValues, emails });
  };

  return {
    downloadFolderMutation,
    initDownloadSetting,
    initProductMapping,
    downloadViaEmails,
    downloadFolder,
  };
};

export const useConfigSchedule = (props) => {
  const { form, folderId, closeModal } = props;

  const scheduleConfigMutation = useMutation({
    mutationKey: ['schedule-download-config'],
    mutationFn: async (params) => {
      try {
        return configScheduleDownload(params);
      } catch (error) {
        console.log('error: ', error);
      }
    },
    meta: {
      invalidates: [getManageScheduleDownloadKey(folderId)],
    },
  });

  const reloadPage = useDispatchReloadPage();

  const initScheduleDownload = (scheduleSetup) => {
    if (!scheduleSetup) return;

    const updateValues = {
      ...scheduleSetup,
      runAt: moment(scheduleSetup.runAt),
    };

    form.setFieldsValue(updateValues);
  };

  const reloadFolderGrid = () => {
    const eventGrid = new CustomEvent(EVENT.RELOAD_FOLDER_GRID);
    document.dispatchEvent(eventGrid);

    const eventDetail = new CustomEvent(EVENT.RELOAD_FOLDER_DETAIL);
    document.dispatchEvent(eventDetail);

    reloadPage({ clearSelection: false });
  };

  const scheduleDownload = async (formParams) => {
    try {
      const response = await scheduleConfigMutation.mutateAsync(formParams);

      if (response?.isSuccess) {
        CustomNotification.success(
          response?.message || 'Config Schedule Download Successfully'
        );

        reloadFolderGrid();

        closeModal();
        return;
      }

      CustomNotification.error(
        response?.message || 'Config Schedule Download Fail'
      );
    } catch (error) {
      console.log('error: ', error);
    }
  };

  return { scheduleConfigMutation, scheduleDownload, initScheduleDownload };
};
